<template>
  <div>
    <!-- download -->
    <slot name="custom-content" :handle-download="handleDownload" :loading="loading">
      <v-list-item
        :class="{ 'pa-0': hasQualityAvailable }"
        :disabled="loading"
        @click="handleDownload()"
      >
        <!-- has qualities available -->
        <template v-if="hasQualityAvailable">
          <!-- title -->
          <template>
            <v-list-item dense class="pa-0 px-3">
              <v-list-item-content class="ml-1">
                <v-list-item-title>
                  {{
                    loading
                      ? $t('message.downloadingFiles')
                      : $t('message.download')
                  }}
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action style="margin: 0px !important">
                <v-progress-circular
                  v-if="loading"
                  :size="18"
                  width="2"
                  color="gray"
                  indeterminate
                />
                <v-icon v-else color="grey lighten-1">mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>
          </template>
        </template>
  
        <!-- don't have qualities available -->
        <template v-else>
          <v-list-item-title>
            <div class="d-flex align-center">
              <span>
                {{
                  loading ? $t('message.downloadingFiles') : $t('message.download')
                }}
              </span>
            </div>
          </v-list-item-title>
          <v-list-item-action style="margin: 0px !important">
            <v-progress-circular
              v-if="loading"
              :size="18"
              width="2"
              color="gray"
              indeterminate
            />
          </v-list-item-action>
        </template>
      </v-list-item>
    </slot>
    <DownloadVideoModal
      :openDownloadVideoModal="openDownloadVideoModal"
      :videoName="video.title"
      :videoExtension="video.extension"
      :videoId="video.id"
      :availableAudios="availableAudios"
      :availableVideoQualities="availableVideoQualities"
      :originalLang="originalLang"
      :downloadLoadingStatus="downloadLoadingStatus"
      :storageSizeDetails="storageSizeDetails"
      :preDownloadWarning="preDownloadWarning"
    ></DownloadVideoModal>
  </div>
</template>

<script>
// external imports
import _ from 'lodash'

// API
import videosApi from '@/services/videosApi'

// components
import DownloadVideoModal from './DownloadVideoModal.vue'

export default {
  name: 'MenuItemDownload',
  components: {
    DownloadVideoModal,
  },
  props: {
    video: {
      type: Object,
      required: true,
    },
    hasRecuo: {
      type: Boolean,
      default: true,
      required: false,
    },
    timeout: {
      type: Number,
      default: 50,
      required: false,
    },
    videoId: {
      type: [Number, String],
      default: '',
      required: true,
    },
    fetchOnMount: {
      type: Boolean,
      default: true,
      required: false,
    },
    value: {
      type: Boolean,
      default: true,
      required: false,
    },
    downloadLoadingStatus: {
      type: Boolean,
      default: false,
    },
    preDownloadWarning: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    sizes: [],
    videoSizes: ['240p', '360p', '480p', '720p', '1080p', '1440p', '2160p'],
    availableResolutionsShortened: [],
    storageSizeDetails: {},
    playback: [],
    downloadServer: null,
    authToken: null,
    qualityToBeDownloaded: null,
    loading: false,
    listFetched: false,
    showQualitiesMenu: false,
    position: {
      x: 0,
      y: 0,
    },
    openDownloadVideoModal: null,
    originalLang: '',
    availableAudios: [],
    availableVideoQualities: [],
  }),
  computed: {
    hasQualityAvailable() {
      const found = _.find(this.sizesList, { available: true })
      return !!found
    },
    sizesList() {
      return [...this.sizes, ...this.extraResolutions]
        .sort((a, b) => {
          const _a = parseInt((a.name ?? a).replace('p', ''))
          const _b = parseInt((b.name ?? b).replace('p', ''))
          return _a - _b
        })
        .map((size) => {
          if (size.name) return { ...size, available: true }
          else return { name: size, available: false }
        })
    },
    extraResolutions() {
      let arr = []
      this.videoSizes.map(
        function (val) {
          let valIndex = this.availableResolutionsShortened.indexOf(val)
          if (valIndex === -1) arr.push(val)
        }.bind(this)
      )
      return arr
    },
  },
  watch: {
    value() {
      this.reset()
    },
    async videoId(value) {
      if (value && this.fetchOnMount) await this.updateList()
    },
  },
  async mounted() {
    if (this.videoId && this.fetchOnMount) await this.updateList()
    this.$root.$on('closeDownloadVideoModal', () => {
      this.handleOpenDownloadVideoModal(false)
    })
    this.$root.$on('handleVideoDownload', (url) => {
      this.$emit('submitVideoDownload', url)
    })
    this.$root.$on('openModalDownloadVideo', () => {
      this.handleOpenDownloadVideoModal(true)
    })
  },
  beforeDestroy() {
    this.$root.$off('closeDownloadVideoModal')
    this.$root.$off('handleVideoDownload')
    this.$root.$off('openModalDownloadVideo')
  },
  methods: {
    async handleDownload() {
      if (this.listFetched) {
        return this.handleOpenDownloadVideoModal(this.listFetched)
      }
      if (!this.fetchOnMount && !this.listFetched) {
        this.$emit('input', true)
        return await this.updateList()
      }
    },
    downloadAlert() {
      this.$root.$emit('DownloadAlertDialog')
    },
    getStandardResolutionValue(value) {
      const [value_1, value_2] = value.split('x')
      const [width, height] = [Number(value_1), Number(value_2)]

      return Math.min(width, height)
    },
    async updateList() {
      this.loading = true

      try {
        const response = await videosApi.getVideoStorageSize(this.videoId)
        const audios = response.data.audios ?? []
        const lipsyncInfo = response.data.config?.lipsync_info ?? []
        const lipsyncLangs = new Set(lipsyncInfo.map(item => item.srclang))

        const audiosWithoutLipsync = audios.filter(
          audio => !lipsyncLangs.has(audio.lang) && audio.status === 'CONVERTED'
        )

        const validLipsyncs = lipsyncInfo.filter(item => item.video_id)

        const lipsyncAudios = validLipsyncs.map(item => ({
          lang: item.srclang,
          label: item.label,
          is_lipsync: true,
          video_id: item.video_id,
          status: 'CONVERTED',
          type: 'GENERATED',
        }))

        this.availableAudios = [...audiosWithoutLipsync, ...lipsyncAudios]

        this.originalLang =
          this.video.original_lang === 'pt' ? 'pt-BR' : this.video.original_lang
        this.storageSizeDetails = response.data.storage_size_details

        let availableResolutionsShortened = []
        let numberResolutions = []

        let sizes = Object.keys(this.storageSizeDetails).map((item) => {
          let name

          if (!~item.indexOf('x')) {
            name = item[0].toUpperCase() + item.slice(1)
          } else {
            name = this.getStandardResolutionValue(item)
            numberResolutions.push(Number(name))
            name = name + 'p'
            availableResolutionsShortened.push(name)
          }

          return {
            name,
            value: this.storageSizeDetails[item],
          }
        })

        this.availableResolutionsShortened = availableResolutionsShortened

        this.playback = response.data.playback.filter((playbackQuality) => {
          let indexOfAvailable =
            availableResolutionsShortened.indexOf(playbackQuality)
          if (indexOfAvailable !== -1) return playbackQuality
        })

        sizes.push({
          name: 'Total',
          value: Object.values(this.storageSizeDetails).reduce(
            (previous, current) => previous + current,
            0
          ),
        })
        sizes = sizes.sort((a, b) =>
          Number(a.name.replace('p', '')) < Number(b.name.replace('p', ''))
            ? 1
            : -1
        )
        sizes = sizes.reverse()

        sizes = sizes.filter((obj) => {
          if (
            obj.name !== 'Total' &&
            obj.name !== 'Backup' &&
            obj.name !== 'Images'
          ) {
            return obj
          }

          return false
        })

        sizes = sizes.map((size) => {
          if (this.playback.indexOf(size.name) === -1) {
            size.disabled = true
          }
          return size
        })

        if (this.sizes.length) {
          this.sizes = this.sizes.map((size, index) => {
            const updateObject = sizes[index]
            updateObject.progressTs = size.progressTs
            updateObject.statusTs = size.statusTs
            updateObject.statusM3u8 = size.statusM3u8
            return updateObject
          })
        } else {
          this.sizes = sizes
        }
        sizes.forEach((obj) => {
          this.availableVideoQualities.push(obj.name)
          this.availableVideoQualities = this.sortResolutionsDescending(
            this.availableVideoQualities
          )
        })
        this.listFetched = true
        this.$emit('fetched')
        this.handleOpenDownloadVideoModal(true)
      } catch (err) {
        console.log(err)
      } finally {
        this.loading = false
      }
    },
    handleOpenDownloadVideoModal(openModal) {
      this.openDownloadVideoModal = openModal
    },
    sortResolutionsDescending(array) {
      function resolutionValue(resolution) {
        const num = parseInt(resolution.replace(/\D/g, ''))
        return isNaN(num) ? 0 : num
      }
      return array.sort(function (a, b) {
        return resolutionValue(b) - resolutionValue(a)
      })
    },
    reset() {
      this.sizes = []
      this.videoSizes = [
        '240p',
        '360p',
        '480p',
        '720p',
        '1080p',
        '1440p',
        '2160p',
      ]
      this.availableResolutionsShortened = []
      this.storageSizeDetails = {}
      this.playback = []
      this.downloadServer = null
      this.authToken = null
      this.qualityToBeDownloaded = null
      this.listFetched = false
    },
  },
}
</script>

<style lang="scss" scoped>
$size: 1.35rem;

.v-list-item__title {
  font-size: 12px !important;
  font-style: normal !important;
  font-weight: 400 !important;
  line-height: 150% !important; /* 18px */
  color: varFigma(neutral-30) !important;
}

.bg-dark {
  .helper {
    &__btn {
      --helper-background-color: rgba(255, 255, 255, 0.54);
      --helper-foreground-color: #000;
    }
  }
}

.helper {
  &__btn {
    --helper-background-color: var(--v-neutral-lighten1);
    --helper-foreground-color: var(--v-neutral-lighten4);

    &::v-deep {
      .v-icon {
        background-color: var(--helper-background-color);
        width: $size;
        height: $size;
        border-radius: 100%;

        &[class*='mdi-'] {
          font-size: $size * 0.8;

          &::before {
            color: var(--helper-foreground-color);
          }
        }
      }
    }
  }
}
</style>
